import type { Command } from './interfaces/command.interface.js';

/**
 * A composite command groups multiple commands into a single atomic operation.
 *
 * `apply` invokes children in declared (forward) order; `revert` invokes them
 * in reverse order so that `revert(apply(s)) ≡ s` holds whenever it holds for
 * each child individually.
 *
 * Composites may be nested: a composite is itself a Command<TState>.
 */
export class CompositeCommand<TState> implements Command<TState> {
  readonly label?: string;

  private readonly children: ReadonlyArray<Command<TState>>;

  constructor(children: ReadonlyArray<Command<TState>>, label?: string) {
    this.children = children;
    this.label = label;
  }

  apply(state: TState): void {
    for (const child of this.children) {
      child.apply(state);
    }
  }

  revert(state: TState): void {
    for (let i = this.children.length - 1; i >= 0; i--) {
      const child = this.children[i];
      if (child !== undefined) {
        child.revert(state);
      }
    }
  }
}
